import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Wrench, Scale, Stethoscope, Building2, Users, Briefcase,
  ArrowRight, ChevronRight, Bot, MessageSquare, Calendar, Target,
  CheckCircle, Star
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PublicHeader } from '@/components/common/PublicHeader';
import { PublicFooter } from '@/components/common/PublicFooter';

const industries = [
  {
    id: 'plumbers',
    name: 'Plumbing Businesses',
    title: 'AI for Plumbers',
    icon: Wrench,
    color: 'from-teal-600 to-cyan-600',
    description: 'Never miss a lead. 24/7 AI assistant that qualifies inquiries, books service appointments, and handles after-hours messages.',
    stats: { messages: '500+', saved: '$3,200/mo', conversion: '+45%' }
  },
  {
    id: 'lawyers',
    name: 'Law Firms',
    title: 'AI for Lawyers',
    icon: Scale,
    color: 'from-slate-700 to-slate-800',
    description: 'Professional intake automation. Qualify potential clients, capture case details, and schedule consultations 24/7.',
    stats: { messages: '300+', saved: '$4,500/mo', conversion: '+38%' }
  },
  {
    id: 'clinics',
    name: 'Medical Clinics',
    title: 'AI for Clinics',
    icon: Stethoscope,
    color: 'from-emerald-500 to-emerald-600',
    description: 'HIPAA-aware patient intake. Handle appointment requests, answer FAQs, and manage patient communications securely.',
    stats: { messages: '800+', saved: '$5,100/mo', conversion: '+52%' }
  },
  {
    id: 'real-estate',
    name: 'Real Estate',
    title: 'AI for Real Estate',
    icon: Building2,
    color: 'from-amber-500 to-orange-500',
    description: 'Capture every lead. Qualify buyers, schedule viewings, and provide property information around the clock.',
    stats: { messages: '450+', saved: '$2,800/mo', conversion: '+41%' }
  },
  {
    id: 'consultants',
    name: 'Consultants',
    title: 'AI for Consultants',
    icon: Users,
    color: 'from-purple-500 to-purple-600',
    description: 'Scale your intake. Let AI handle initial inquiries, qualify prospects, and book discovery sessions automatically.',
    stats: { messages: '200+', saved: '$2,400/mo', conversion: '+35%' }
  },
  {
    id: 'agencies',
    name: 'Agencies',
    title: 'AI for Agencies',
    icon: Briefcase,
    color: 'from-rose-500 to-rose-600',
    description: 'White-label AI assistant for your clients. Multi-tenant support, custom branding, and scalable lead management.',
    stats: { messages: '1,000+', saved: '$8,500/mo', conversion: '+48%' }
  },
  {
    id: 'dental',
    name: 'Dental Practices',
    title: 'AI for Dental',
    icon: Stethoscope,
    color: 'from-cyan-500 to-teal-500',
    description: '24/7 appointment booking, insurance pre-qualification, and patient communication. Reduce no-shows by 60%.',
    stats: { messages: '600+', saved: '$3,400/mo', conversion: '+42%' }
  },
  {
    id: 'hvac',
    name: 'HVAC Companies',
    title: 'AI for HVAC',
    icon: Wrench,
    color: 'from-orange-500 to-red-500',
    description: 'Handle emergency inquiries 24/7, route requests to technicians, and book maintenance appointments automatically.',
    stats: { messages: '700+', saved: '$3,900/mo', conversion: '+44%' }
  },
  {
    id: 'auto',
    name: 'Auto Repair Shops',
    title: 'AI for Auto Shops',
    icon: Wrench,
    color: 'from-zinc-600 to-zinc-700',
    description: 'Book service appointments, provide estimates, and capture vehicle information while techs focus on repairs.',
    stats: { messages: '350+', saved: '$3,100/mo', conversion: '+39%' }
  },
  {
    id: 'salons',
    name: 'Salons & Spas',
    title: 'AI for Salons',
    icon: Users,
    color: 'from-pink-500 to-rose-500',
    description: '24/7 appointment booking, stylist matching, and automated reminders. Never interrupt a service again.',
    stats: { messages: '400+', saved: '$2,700/mo', conversion: '+46%' }
  },
  {
    id: 'fitness',
    name: 'Fitness & Gyms',
    title: 'AI for Fitness',
    icon: Users,
    color: 'from-green-500 to-emerald-600',
    description: 'Convert more leads, handle membership inquiries, and book classes 24/7. Scale without hiring more staff.',
    stats: { messages: '550+', saved: '$3,000/mo', conversion: '+40%' }
  }
];

const IndustriesPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <PublicHeader />

      {/* Hero Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 bg-slate-50 text-slate-900 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Target className="w-4 h-4" />
              Industry-Specific AI Solutions
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              AI Assistant Built for{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-cyan-500">
                Your Industry
              </span>
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-8">
              Every business is unique. That's why we've trained specialized AI assistants that understand 
              your industry's terminology, workflows, and customer expectations.
            </p>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-4 text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
              <span>Industry-specific training</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
              <span>24/7 availability</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
              <span>No technical skills required</span>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-12">
            Choose Your Industry
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map((industry, index) => (
              <motion.div
                key={industry.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Link 
                  to={`/ai-for-${industry.id}`}
                  className="block bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-xl hover:border-teal-200 transition-all group"
                >
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${industry.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <industry.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-slate-900 transition-colors flex items-center gap-2">
                    {industry.title}
                    <ArrowRight className="w-5 h-5 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </h3>
                  <p className="text-slate-600 mb-4">
                    {industry.description}
                  </p>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1 text-emerald-600">
                      <MessageSquare className="w-4 h-4" />
                      <span>{industry.stats.messages} msgs/mo</span>
                    </div>
                    <div className="flex items-center gap-1 text-slate-900">
                      <Star className="w-4 h-4" />
                      <span>{industry.stats.conversion} leads</span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Why Industry-Specific AI Matters
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Generic AI Employees fail because they don't understand context. A plumber's emergency call 
                needs different handling than a law firm's intake. Our AI is trained on real conversations 
                from your industry.
              </p>
              <ul className="space-y-4">
                {[
                  'Understands industry terminology and jargon',
                  'Pre-trained on common questions and scenarios',
                  'Knows when to escalate vs. when to handle independently',
                  'Integrates with industry-standard tools'
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-teal-50 to-indigo-50 rounded-2xl p-8">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-600 to-cyan-600 flex items-center justify-center">
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">AI Assistant</p>
                    <p className="text-xs text-slate-500">Trained for your industry</p>
                  </div>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="bg-slate-100 rounded-lg p-3 text-slate-700">
                    "I have an emergency - my basement is flooding!"
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 text-slate-700 border-l-4 border-teal-600">
                    "I understand this is urgent. Let me get a plumber dispatched to your location right away. 
                    Can you confirm your address and if you've been able to shut off the water main?"
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-teal-600 to-cyan-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Deploy Your AI Employee?
          </h2>
          <p className="text-xl text-teal-100 mb-8">
            Start your free trial today. No credit card required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/login">
              <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-50 px-8">
                Start Free Trial
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/enterprise">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8">
                Enterprise Solutions
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default IndustriesPage;
